import {
  paddingStyles,
  SectionTitle,
} from "features/bots/BotForm/common/styles";
import React, { CSSProperties, useEffect } from "react";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay, {
  LoadingSpinnerOverlayProps,
} from "common/Overlays/LoadingSpinnerOverlay";
import { useTheme } from "styled-components";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import CardSorters from "common/CardSorters/CardSorters";
import styled from "styled-components";
import RightOutlined from "@ant-design/icons/RightOutlined";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import { SharedSummarySectionProps } from "@shared/features/bots/BotForm/common/types";
import { CardSortersProps } from "@shared/common/CardSorters/types";
import useMeasure from "utils/hooks/useMeasure";
import { useSummarySection } from "@shared/features/bots/BotForm/common/hooks";

interface SummarySectionProps extends SharedSummarySectionProps {
  columnNames: CardSortersProps["columnNames"];
  columnSpans: CardSortersProps["columnSpans"];
  alignLastColumnRight?: boolean;
  sorterStyle?: CSSProperties;
  isCard?: boolean;
  containerStyle?: CSSProperties;
  removeBorderRadiusSide?: "left" | "right";
  setErrorHeight?: (height: number) => void;
  errorsHeight?: number;
  sorterHeight?: number;
  className?: string;
  isSideBySide?: boolean;
  loadingProps?: LoadingSpinnerOverlayProps;
}

const SummarySection = ({
  errorMessage,
  columnNames,
  columnSpans,
  tooltipProps,
  title,
  isFetching,
  alignLastColumnRight,
  sorterStyle,
  isCard = true,
  containerStyle,
  children,
  removeBorderRadiusSide,
  setErrorHeight,
  errorsHeight,
  sorterHeight,
  className,
  isSideBySide,
  loadingProps,
}: SummarySectionProps) => {
  const appTheme = useTheme();
  const [measureRef, { height }] = useMeasure<HTMLDivElement>();
  const { sectionExpanded, onTitleClick, isExpandable } = useSummarySection();

  useEffect(() => {
    setErrorHeight?.(height);
  }, [height]);

  const getTitle = () => {
    if (!title) return;
    const helper = tooltipProps && (
      <HelperTooltip {...tooltipProps} placement="bottomRight" maxWidth={240} />
    );
    const style: CSSProperties = {
      display: "flex",
      alignItems: "center",
    };
    if (isExpandable) {
      style.cursor = "pointer";
      style.justifyContent = "space-between";
    }
    return (
      <SectionTitle onClick={onTitleClick} style={style}>
        <Flex>
          {title} {isExpandable && <Arrow $expanded={sectionExpanded} />}
        </Flex>
        {helper}
      </SectionTitle>
    );
  };

  const getError = () => {
    let errorContent = errorMessage && (
      <ErrorsWrapper $isSideBySide={isSideBySide}>{errorMessage}</ErrorsWrapper>
    );
    if (setErrorHeight && errorContent) {
      errorContent = (
        <div ref={measureRef} style={{ position: "absolute" }}>
          {errorContent}
        </div>
      );
    }
    if (errorsHeight) {
      return (
        <div
          style={{
            height: errorsHeight ?? undefined,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {errorContent}
        </div>
      );
    }
    return errorContent;
  };

  return (
    <Wrapper $isCard={isCard} style={containerStyle} className={className}>
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        backgroundColor={appTheme.colors.layoutBgColor}
        {...loadingProps}
      >
        {getTitle()}
        {getError()}
        <ExpandCollapse expanded={sectionExpanded}>
          <StyledSorters
            columnNames={columnNames}
            columnSpans={columnSpans}
            alignLastColumnRight={alignLastColumnRight}
            sorterStyle={sorterStyle}
            $removeBorderRadiusSide={removeBorderRadiusSide}
            $flushMarginTop={!title || !!errorMessage || !!errorsHeight}
            style={{
              height: isSideBySide ? sorterHeight : undefined,
              display: "flex",
              alignItems: "center",
            }}
          />
          {children}
        </ExpandCollapse>
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default SummarySection;

const Wrapper = styled.div<{ $isCard?: boolean }>`
  position: relative;
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  margin-top: 16px;
  ${paddingStyles};
  ${(p) =>
    p.theme.isNewLook &&
    p.$isCard &&
    `
    padding: 16px;
    border: ${p.theme.borders.panel};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: ${p.theme.shadows.panel};
    background: ${p.theme.colors.cardBgColor};
  `};
  ${(p) =>
    !p.$isCard &&
    `
    padding: 0 0 20px 0;
    border: none;
    margin-top: 0;
  `};
`;
const ErrorsWrapper = styled(Text).attrs({
  type: "danger",
  $size: typography.fontSizes.S,
  as: "div",
})<{ $isSideBySide?: boolean }>`
  border-left: 2px solid ${(p) => p.theme.colors.red};
  padding: 4px 0 4px 12px;
  margin: ${(p) => (p.$isSideBySide ? 8 : 0)}px 0 12px 0;
`;
const Arrow = styled(RightOutlined)<{ $expanded: boolean }>`
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  margin-left: 8px;
  transition: transform 0.35s;
  display: block;
`;
const StyledSorters = styled(CardSorters)<{
  $removeBorderRadiusSide?: "right" | "left";
  $flushMarginTop?: boolean;
}>`
  padding: 6px 12px;
  margin: ${(p) => (p.$flushMarginTop ? 0 : 12)}px 0 12px 0;
  ${(p) =>
    p.theme.isNewLook &&
    `
    background-color: ${p.theme.colors.cardLightBgColor};
    border-radius: 10px;
  `}
  ${(p) =>
    p.$removeBorderRadiusSide &&
    `
    border-top-${p.$removeBorderRadiusSide}-radius: 0;
    border-bottom-${p.$removeBorderRadiusSide}-radius: 0;
  `}
`;
